import { useState } from "react";

import { LockOutlined, UserOutlined } from "@ant-design/icons";
import {
	Alert,
	Button,
	Card,
	Flex,
	Form,
	Image,
	Input,
	Typography,
} from "antd";

import type { LoginComponentProps } from "@neu/libui";

import styles from "./Login.module.scss";

export type LoginProps = LoginComponentProps;

export function Login({ onLogin, onForgotPassword }: LoginProps) {
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	const handleSubmit = async (values: {
		username: string;
		password: string;
	}) => {
		setLoading(true);
		setError(null);

		try {
			await onLogin(values.username, values.password);
		} catch (err: any) {
			setError(err.message || "Login failed");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div
			style={{
				display: "flex",
				justifyContent: "center",
				alignItems: "center",
				minHeight: "100vh",
			}}
		>
			<Card className={styles.loginCard}>
				<div className={styles.logoContainer}>
					<Image
						src="/logo.png"
						alt="Neu Application Logo"
						preview={false}
						className={styles.appLogo}
					/>
				</div>

				{error && (
					<Alert
						message="Login Failed"
						description={error}
						type="error"
						showIcon
						closable
						onClose={() => {
							setError(null);
						}}
						style={{ marginBottom: 24, borderRadius: 8 }}
					/>
				)}

				<Form
					name="login"
					onFinish={(values: {
						username: string;
						password: string;
					}) => void handleSubmit(values)}
					autoComplete="off"
					layout="vertical"
				>
					<Form.Item
						name="username"
						rules={[
							{
								required: true,
								message: "Please enter your username",
							},
						]}
						style={{ marginBottom: 10 }}
					>
						<Input
							prefix={
								<UserOutlined
									style={{
										color: "var(--color-primary-600)",
									}}
								/>
							}
							placeholder="Username"
							size="large"
							autoFocus
							className={styles.userNameInput}
						/>
					</Form.Item>

					<Form.Item
						name="password"
						rules={[
							{
								required: true,
								message: "Please enter your password",
							},
						]}
						style={{ marginBottom: 10 }}
					>
						<Input.Password
							prefix={
								<LockOutlined
									style={{
										color: "var(--color-primary-600)",
									}}
								/>
							}
							placeholder="Password"
							size="large"
							className={styles.passwordInput}
						/>
					</Form.Item>

					{onForgotPassword && (
						<Flex justify="flex-end" style={{ marginBottom: 8 }}>
							<Typography.Link onClick={onForgotPassword}>
								Forgot password?
							</Typography.Link>
						</Flex>
					)}

					<Form.Item style={{ marginTop: 8 }}>
						<Button
							type="primary"
							size="large"
							htmlType="submit"
							loading={loading}
							block
							style={{
								borderRadius: 12,
								height: 42,
							}}
						>
							Login with username
						</Button>
					</Form.Item>
				</Form>
			</Card>
		</div>
	);
}

export default Login;
